import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Box,
  IconButton,
  InputAdornment,
  ListItemButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "../../../assets/icons/closeIcon.svg";
import "./template-list-popup.scss";
import PreviewSampleImage from "../../../assets/images/previewSampleImage.png";
import SearchInputComponent from "../search-input-component/search-input-component";
import _ from "lodash";
import { CircularProgress } from "@mui/material";
interface Template {
  id: string;
  name: string;
}

interface TemplateListPopupProps {
  open: boolean;
  onClose: () => void;
  onSelect: (templateId: string | null) => void;
  templates: Template[];
  title?: string;
  selectedTemplateData?: any;
  setSelectedTemplateData?: any;
}

const TemplateListPopup: React.FC<TemplateListPopupProps> = ({
  open,
  onClose,
  onSelect,
  templates,
  title = "",
  selectedTemplateData,
  setSelectedTemplateData,
}) => {
  const [search, setSearch] = useState("");
  const searchRef = useRef<any>(null);
  const [filteredTemplates, setFilteredTemplates] =
    useState<Template[]>(templates);
  const [iframeLoading, setIframeLoading] = useState(false);

  const [letterPdfUrl, setLetterPdfUrl] = useState<string | null>(null);

  const handleSearch = useCallback(
    _.debounce((text: string) => {
      const filtered = templates.filter((template) =>
        template.name.toLowerCase().includes(text.toLowerCase())
      );
      setFilteredTemplates(filtered);
    }, 300),
    [templates]
  );

  const onSearchChange = (text: string) => {
    setSearch(text); // update search field immediately
    handleSearch(text); // debounce filtering
  };

  const resetSearch = () => {
    setSearch("");
    if (searchRef.current) {
      searchRef.current.clear();
    }
  };

  useEffect(() => {
    handleSearch(search);
  }, [templates]);

  useEffect(() => {
    if (open) {
      resetSearch(); // Reset search when popup opens
      setFilteredTemplates(templates); // Reset the filtered list
      
    }
  }, [open, templates]);

  useEffect(() => {
    if (selectedTemplateData?.template_type === "Letter") {
      if (selectedTemplateData?.preview_url_s3) {
        const encodedFileUrl = encodeURIComponent(
          selectedTemplateData?.preview_url_s3 || ""
        );
        const googleDocsViewerUrl = `https://docs.google.com/viewer?embedded=true&url=${encodedFileUrl}`;
        console.log("Google Docs Viewer URL:", googleDocsViewerUrl);
        setLetterPdfUrl(googleDocsViewerUrl);
      } else {
        setLetterPdfUrl(null);
      }
    } else setLetterPdfUrl(null);
  }, [selectedTemplateData]);

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="lg"
      className="update-popup-wrapper template-list-popup"
    >
      <DialogTitle>
        <div className="header-wrapper">
          {title}
          <img
            src={CloseIcon}
            alt="Close"
            onClick={onClose}
            className="close-icon"
          />
        </div>
      </DialogTitle>
      <DialogContent dividers>
        <Box className="alert-dialog-templates-list-wrapper">
          {/* Left Panel: Template List */}
          <Box width="50%" pr={2} overflow="auto" borderRight="1px solid #ddd">
            <SearchInputComponent
              placeholder="Search Templates"
              onSearch={onSearchChange}
              ref={searchRef}
            />
            <List className="alert-dialog-template-list">
              {filteredTemplates.length === 0 && search ? (
                <ListItem
                  sx={{ alignItems: "center", justifyContent: "center" }}
                  className="no-records-wrapper"
                >
                  <ListItemText primary="No templates found" />
                </ListItem>
              ) : null}
              {filteredTemplates?.map((template) => (
                <React.Fragment key={template.id}>
                  <ListItemButton
                    selected={selectedTemplateData?.id === template.id}
                    className="alert-dialog-template-item"
                    onClick={() => {
                      setSelectedTemplateData(template);
                    }}
                  >
                    <ListItemIcon>
                      <Checkbox
                        checked={selectedTemplateData?.id === template.id}
                        onChange={() =>
                          setSelectedTemplateData(
                            selectedTemplateData?.id === template.id
                              ? null
                              : template
                          )
                        }
                        onClick={(e) => e.stopPropagation()}
                      />
                    </ListItemIcon>
                    <ListItemIcon>
                      <img
                        src={PreviewSampleImage}
                        alt={template.name}
                        className="template-preview-icon-image"
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={template.name}
                      className={`${selectedTemplateData?.id === template?.id ? "active-template-text" : "template-name-text"}`}
                    />
                  </ListItemButton>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          </Box>

          {/* Right Panel: Template Preview Placeholder */}
          <Box width="50%" pl={2} className="alert-dialog-preview-pane">
            {/* Placeholder – You can replace this with file preview */}
            <Box
              border="1px solid #ccc"
              borderRadius="8px"
              height="100%"
              display="flex"
              alignItems="center"
              justifyContent="center"
              color="#999"
              className="alert-dialog-preview-box"
              boxSizing={'border-box'}
            >
              {selectedTemplateData?.preview_url_s3 &&
                (selectedTemplateData?.template_type === "Email" ? (
                  <img
                    src={selectedTemplateData?.preview_url_s3}
                    alt={selectedTemplateData?.name}
                    className="template-preview-image"
                  />
                ) : (
                  <>
                    {" "}
                    {letterPdfUrl && letterPdfUrl !== null && (
                      <>
                        {iframeLoading && (
                          <div className="file-loader-spinner">
                            <CircularProgress />
                          </div>
                        )}
                        <iframe
                          src={letterPdfUrl || "about:blank"}
                          title="File Preview"
                          width="100%"
                          height="590px"
                          style={{ border: "none", overflowY: "auto" }}
                          onLoad={() => setIframeLoading(false)}
                        />
                      </>
                    )}
                  </>
                ))}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions className="button-wrapper template-list-popup-buttons-wrapper">
        <Button onClick={onClose} className="cancel-btn">
          Cancel
        </Button>
        <Button
          disabled={!selectedTemplateData?.id}
          onClick={() => onSelect(selectedTemplateData)}
          className="update-btn select-template-btn"
        >
          Select Template
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateListPopup;
